/**
 * AppConfig Entity
 *
 * Represents the platform-level application configuration
 */

export interface AppConfigProps {
  id: number;
  appName: string;
  appTagline?: string;
  // Light mode logos
  logoOriginalUrl?: string;
  logoFaviconUrl?: string;
  logoSmallUrl?: string;
  logoMediumUrl?: string;
  logoLargeUrl?: string;
  // Dark mode logos
  logoDarkOriginalUrl?: string;
  logoDarkFaviconUrl?: string;
  logoDarkSmallUrl?: string;
  logoDarkMediumUrl?: string;
  logoDarkLargeUrl?: string;
  primaryColor?: string;
  updatedAt?: Date;
  updatedBy?: string;
}

export class AppConfig {
  readonly id: number;
  readonly appName: string;
  readonly appTagline?: string;
  // Light mode logos
  readonly logoOriginalUrl?: string;
  readonly logoFaviconUrl?: string;
  readonly logoSmallUrl?: string;
  readonly logoMediumUrl?: string;
  readonly logoLargeUrl?: string;
  // Dark mode logos
  readonly logoDarkOriginalUrl?: string;
  readonly logoDarkFaviconUrl?: string;
  readonly logoDarkSmallUrl?: string;
  readonly logoDarkMediumUrl?: string;
  readonly logoDarkLargeUrl?: string;
  readonly primaryColor?: string;
  readonly updatedAt?: Date;
  readonly updatedBy?: string;

  constructor(props: AppConfigProps) {
    this.id = props.id;
    this.appName = props.appName;
    this.appTagline = props.appTagline;
    this.logoOriginalUrl = props.logoOriginalUrl;
    this.logoFaviconUrl = props.logoFaviconUrl;
    this.logoSmallUrl = props.logoSmallUrl;
    this.logoMediumUrl = props.logoMediumUrl;
    this.logoLargeUrl = props.logoLargeUrl;
    this.logoDarkOriginalUrl = props.logoDarkOriginalUrl;
    this.logoDarkFaviconUrl = props.logoDarkFaviconUrl;
    this.logoDarkSmallUrl = props.logoDarkSmallUrl;
    this.logoDarkMediumUrl = props.logoDarkMediumUrl;
    this.logoDarkLargeUrl = props.logoDarkLargeUrl;
    this.primaryColor = props.primaryColor;
    this.updatedAt = props.updatedAt;
    this.updatedBy = props.updatedBy;
  }

  /**
   * Check if a custom logo has been uploaded (light mode)
   */
  hasCustomLogo(): boolean {
    return !!this.logoOriginalUrl;
  }

  /**
   * Check if a dark mode logo has been uploaded
   */
  hasCustomDarkLogo(): boolean {
    return !!this.logoDarkOriginalUrl;
  }

  /**
   * Get logo URL for a specific size (light mode)
   */
  getLogoForSize(size: 'favicon' | 'small' | 'medium' | 'large' | 'original'): string | undefined {
    switch (size) {
      case 'favicon':
        return this.logoFaviconUrl;
      case 'small':
        return this.logoSmallUrl;
      case 'medium':
        return this.logoMediumUrl;
      case 'large':
        return this.logoLargeUrl;
      case 'original':
        return this.logoOriginalUrl;
    }
  }

  /**
   * Get dark mode logo URL for a specific size
   */
  getDarkLogoForSize(size: 'favicon' | 'small' | 'medium' | 'large' | 'original'): string | undefined {
    switch (size) {
      case 'favicon':
        return this.logoDarkFaviconUrl;
      case 'small':
        return this.logoDarkSmallUrl;
      case 'medium':
        return this.logoDarkMediumUrl;
      case 'large':
        return this.logoDarkLargeUrl;
      case 'original':
        return this.logoDarkOriginalUrl;
    }
  }
}
